// ============================================================
// Projektabschluss, Rückblick und „Gelernt" (SPEC §51.3, Wanderung 046).
//
// Drei Dinge, die zusammengehören:
//   - die Abschluss-Checkliste im Reiter Stand (fünf Haken, von Hand; die
//     Hinweise daneben rechnet der Server),
//   - der Rückblick am Ende einer Leistungsphase (LPH 3, 5, 8) oder des
//     Projekts (FIN) — gespeichert als `Rueckblick` UND als Beitrag im
//     Fragen-Faden des Projekts; der Anstoß „Kurzer Rückblick?" kommt ruhig im
//     Cockpit (Tafel Rueckblick),
//   - die hausweite Sammlung „Gelernt" (die Antworten auf „Was nehmen wir fürs
//     Haus mit?", Quelle = Projekt + Datum, keine Person).
//
// Eine eigene Datei neben `hooks.ts` wie `cockpit.ts`, `terminpunkte.ts`: `hooks.ts`
// ist die meistgeänderte Datei des Frontends. Pfade ohne `/api`.
//
// ---- Drei Festlegungen -------------------------------------------------------
//
// 1. CHECKLISTE UND RÜCKBLICKE LIEGEN UNTER `["projekt", projektId, …]`. Die
//    Tabellen `projekt_abschluss` und `rueckblick` melden sich zeilenweise MIT
//    Projektkennung (046), und `lib/ereignisse.ts` entwertet bei jeder Meldung
//    mit Kennung den Präfix `["projekt", id]` — derselbe Präfix trifft die
//    Hinweise der Checkliste, wenn sich Fristen, Pläne, Bilder oder Abgaben regen,
//    und den Anstoß, wenn ein Termin der Art `rueckblick` angelegt wird.
//
// 2. „GELERNT" LIEGT UNTER `["gelernt"]` — hausweit, ohne Projekt. Der Strom
//    entwertet es bei `rueckblick` und `projekt` (Name, Freigabe, Löschen).
//
// 3. EIN RÜCKBLICK SCHREIBT AUCH EINEN BEITRAG. Jeder Schreibweg entwertet darum
//    die Akte, Aktuelles (`["aktuelles"]`: der Fragen-Faden, der Feed) und
//    „Gelernt" — der Strom täte es auch, aber erst nach 500 ms.
//
// Hausregel (§38.11): `null` aus dem Netz kann als `undefined` ankommen —
// `anstoss`, `termin`, `termin_id`, `beitrag_id`, `erledigt_am` mit `== null`.
// ============================================================
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { api } from "./client";
import { aktuellesSchluessel, schluessel } from "./hooks";
import type {
  AbschlussPunktArt,
  AbschlussPunktSetzen,
  GelerntEintrag,
  ProjektAbschluss,
  Rueckblick,
  RueckblickAendern,
  RueckblickAnlass,
  RueckblickNeu,
  RueckblickStand,
} from "./types";

/* ========================================================== Schlüssel */

export const abschlussSchluessel = {
  /** Die Checkliste EINES Projekts — UNTER `["projekt", id]` (Festlegung 1). */
  stand: (projektId: string) => [...schluessel.projekt(projektId), "abschluss"] as const,
};

export const rueckblickSchluessel = {
  /** Rückblicke und Anstoß EINES Projekts — UNTER `["projekt", id]` (Festlegung 1). */
  stand: (projektId: string) => [...schluessel.projekt(projektId), "rueckblicke"] as const,
};

export const gelerntSchluessel = {
  /** Die hausweite Sammlung (Festlegung 2). */
  alle: ["gelernt"] as const,
};

/* ========================================================== Wege in den Reiter Stand */

/**
 * Der Parameter, mit dem ein Verweis im Reiter Stand einen Abschnitt ansteuert:
 * `/projekte/<id>?reiter=stand&abschnitt=fristen`. Kalender-Abo (Server, `kalender._frist`),
 * Vorschau (Zellenliste) und Cockpit (Meilenstein „Frist") zeigen dorthin; der Reiter Stand
 * rollt den Abschnitt beim Öffnen ins Bild.
 */
export const STAND_ABSCHNITT_PARAM = "abschnitt";
/** Die Abschnitte, die §51 im Reiter Stand anlegt (Reihenfolge im Reiter: §51.8). */
export type StandAbschnitt = "abschluss" | "rueckblicke" | "fristen" | "baukosten";
export const STAND_ABSCHNITTE: readonly StandAbschnitt[] = ["abschluss", "rueckblicke", "fristen", "baukosten"];

/** `/projekte/<id>?reiter=stand` — mit Abschnitt `…&abschnitt=<abschnitt>`. */
export function standWeg(projektId: string, abschnitt?: StandAbschnitt): string {
  const basis = `/projekte/${encodeURIComponent(projektId)}?reiter=stand`;
  return abschnitt ? `${basis}&${STAND_ABSCHNITT_PARAM}=${abschnitt}` : basis;
}

/** Ist dieser Wert ein Abschnitt aus §51? Für das Lesen von `?abschnitt=` (fremde Werte: nichts tun). */
export function istStandAbschnitt(wert: string | null | undefined): wert is StandAbschnitt {
  return wert != null && (STAND_ABSCHNITTE as readonly string[]).includes(wert);
}

/* ========================================================== Zahlen (dieselben wie im Server, §51.3) */

/** Jede der drei Antworten: höchstens so viele Zeichen (CHECK in 046); der Server kürzt. */
export const RUECKBLICK_TEXT_MAX = 1000;
/** Höchstens so viele Erfolgskriterien — so viele trägt `Projekt.erfolg` (§39.1). */
export const RUECKBLICK_KRITERIEN_MAX = 3;
/** Der Anstoß kommt nur so viele Tage nach dem Phasenwechsel (ohne geplanten Rückblick-Termin). */
export const RUECKBLICK_FENSTER_TAGE = 42;
/** Ein geplanter Rückblick-Termin hält den Anstoß bis so viele Tage nach seinem Tag offen. */
export const RUECKBLICK_NACHLAUF_TAGE = 14;
/** So viele Einträge bringt `GET /gelernt` höchstens. */
export const GELERNT_MAX = 500;
/** Die Länge des Termins, den „Rückblick planen" anlegt (Tafel: „30 Minuten, drei Fragen"). */
export const RUECKBLICK_TERMIN_MINUTEN = 30;

/**
 * Der Merker für „Später" am Anstoß (§51.3): je Person im Browser, je Projekt UND Phase — ein
 * „Später" am Ende von LPH 3 schweigt nicht auch das Ende von LPH 5 tot. Über `lib/storage`
 * (Präfix `lp.`), Wert `true`. Kein Server: es ist eine Bequemlichkeit, keine Entscheidung.
 */
export function spaeterSchluessel(projektId: string, phaseId: string): string {
  return `rueckblick.spaeter.${projektId}.${phaseId}`;
}

/**
 * Welche Hauptphase (`phase.schluessel`) auf das Ende welcher Phase schauen lässt (§51.3 c, Punkt 2) —
 * dieselbe Abbildung wie `abschluss.ANSTOSS_NACH_PHASE` im Server (`test_vertrag` vergleicht beide).
 * Ein Projekt in LPH 5 hat LPH 3 hinter sich; LPH 6 → 7 → 8 bleiben beim Anlass `lph5` (der Anstoß
 * beginnt dabei nicht neu, §51.3 c Punkt 4). Die Oberfläche braucht nur die SCHLÜSSEL: ab LPH 4 steht
 * der Abschnitt „Rückblicke" fürs Team auch ohne Rückblick (`zeigtRueckblicke`, §51.8 a).
 */
export const ANSTOSS_NACH_PHASE: Readonly<Record<string, RueckblickAnlass>> = {
  lph4: "lph3",
  lph5: "lph3",
  lph6: "lph5",
  lph7: "lph5",
  lph8: "lph5",
  lph9: "lph8",
  fin: "fin",
};

/** Die Seite „Gelernt" (§51.8 f) — ein eigener Weg, NICHT unter `/aktuelles/…` (dort fängt
 *  `/aktuelles/:beitrag` jeden Abschnitt als Beitragskennung). */
export const GELERNT_PFAD = "/gelernt";
/** Die Filter der Seite, in der Adresse gemerkt (`?projekt=`, `?team=`). */
export const GELERNT_PROJEKT_PARAM = "projekt";
export const GELERNT_TEAM_PARAM = "team";

/** `/gelernt` — mit Projekt `/gelernt?projekt=<id>` (Toast nach dem Festhalten, Verweis im
 *  Rückblick-Beitrag). Leere Kennung wie keine. */
export function gelerntWeg(projektId?: string | null): string {
  return projektId ? `${GELERNT_PFAD}?${GELERNT_PROJEKT_PARAM}=${encodeURIComponent(projektId)}` : GELERNT_PFAD;
}

/** Wie lange eine Antwort als frisch gilt. Der Ereignisstrom entwertet ohnehin. */
const FRISCH_MS = 30_000;

/* ========================================================== Entwerten */

/** Nach einem Haken der Checkliste: die Akte samt Checkliste (Festlegung 1). */
export function abschlussEntwerten(qc: QueryClient, projektId: string): void {
  void qc.invalidateQueries({ queryKey: schluessel.projekt(projektId) });
}

/** Nach jedem Schreibweg eines Rückblicks (Festlegung 3). */
export function rueckblickEntwerten(qc: QueryClient, projektId: string): void {
  void qc.invalidateQueries({ queryKey: schluessel.projekt(projektId) });
  void qc.invalidateQueries({ queryKey: aktuellesSchluessel.alle });
  void qc.invalidateQueries({ queryKey: gelerntSchluessel.alle });
}

/* ========================================================== Checkliste */

/** `GET /projekte/{id}/abschluss` → `ProjektAbschluss`. Lesen darf, wer das Projekt sieht. */
export function useAbschluss(projektId: string | null | undefined, aktiv = true): UseQueryResult<ProjektAbschluss> {
  return useQuery({
    queryKey: abschlussSchluessel.stand(projektId ?? ""),
    queryFn: () => api.get<ProjektAbschluss>(`/projekte/${projektId}/abschluss`),
    enabled: !!projektId && aktiv,
    staleTime: FRISCH_MS,
  });
}

/** Was `useAbschlussPunkt` braucht. */
export interface AbschlussHaken extends AbschlussPunktSetzen {
  punkt: AbschlussPunktArt;
}

/**
 * `PUT /projekte/{id}/abschluss/{punkt}` `{erledigt}` → `ProjektAbschluss`. Recht: das Team (§22).
 * Die Antwort ist die ganze Liste — sie wird gleich abgelegt, entwertet wird trotzdem (Hinweise).
 */
export function useAbschlussPunkt(projektId: string): UseMutationResult<ProjektAbschluss, Error, AbschlussHaken> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ punkt, erledigt }: AbschlussHaken) =>
      api.put<ProjektAbschluss>(`/projekte/${projektId}/abschluss/${punkt}`, { erledigt } satisfies AbschlussPunktSetzen),
    onSuccess: (antwort) => qc.setQueryData(abschlussSchluessel.stand(projektId), antwort),
    onSettled: () => abschlussEntwerten(qc, projektId),
  });
}

/* ========================================================== Rückblick */

/** `GET /projekte/{id}/rueckblicke` → `RueckblickStand` (Rückblicke + Anstoß). */
export function useRueckblicke(projektId: string | null | undefined, aktiv = true): UseQueryResult<RueckblickStand> {
  return useQuery({
    queryKey: rueckblickSchluessel.stand(projektId ?? ""),
    queryFn: () => api.get<RueckblickStand>(`/projekte/${projektId}/rueckblicke`),
    enabled: !!projektId && aktiv,
    staleTime: FRISCH_MS,
  });
}

/** `POST /projekte/{id}/rueckblicke` → 201 `Rueckblick` — dazu ein Beitrag im Fragen-Faden. */
export function useRueckblickNeu(projektId: string): UseMutationResult<Rueckblick, Error, RueckblickNeu> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: RueckblickNeu) => api.post<Rueckblick>(`/projekte/${projektId}/rueckblicke`, b),
    onSuccess: () => rueckblickEntwerten(qc, projektId),
  });
}

/** `PATCH /rueckblicke/{id}` → `Rueckblick` — schreibt den Beitrag neu, solange es ihn gibt. */
export function useRueckblickAendern(
  projektId: string,
): UseMutationResult<Rueckblick, Error, RueckblickAendern & { id: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...b }: RueckblickAendern & { id: string }) =>
      api.patch<Rueckblick>(`/rueckblicke/${id}`, b satisfies RueckblickAendern),
    onSuccess: () => rueckblickEntwerten(qc, projektId),
  });
}

/** `DELETE /rueckblicke/{id}` → 204 — sein Beitrag geht mit (weich gelöscht). */
export function useRueckblickLoeschen(projektId: string): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<void>(`/rueckblicke/${id}`),
    onSuccess: () => rueckblickEntwerten(qc, projektId),
  });
}

/* ========================================================== Gelernt */

/** `GET /gelernt` → `GelerntEintrag[]` (neueste zuerst, höchstens `GELERNT_MAX`). Gefiltert wird im
 *  Client (Projekt, Team) — es sind ihrer Natur nach wenige. */
export function useGelernt(aktiv = true): UseQueryResult<GelerntEintrag[]> {
  return useQuery({
    queryKey: gelerntSchluessel.alle,
    queryFn: () => api.get<GelerntEintrag[]>("/gelernt"),
    enabled: aktiv,
    staleTime: 60_000,
  });
}
